import { createContext, useContext } from "react";

import {
  OPCIONES_DE_TEMA,
  TEMA_ESTANDAR,
  VIDRIO_MIN,
  temaDeLaOpcion,
  varianteDeLaOpcion,
} from "./temasUat";
import { AMBIENTES, FOTO_SIN_AMBIENTE } from "../../shared/ambiente";

// Los temas con foto o ambiente detrás: los únicos donde la opacidad de las
// tarjetas cambia algo (es la misma lista que usa la hoja).
const CON_FONDO = [...Object.keys(AMBIENTES), ...FOTO_SIN_AMBIENTE];

/* EL SELECTOR DE TEMAS, EN PREFERENCIAS (UAT).
 *
 * Con un tema puesto, Preferencias esconde «Automático / Claro / Oscuro» y el
 * color de acento, porque las dos cosas las dice el tema. En ese hueco va lo
 * que sí se puede elegir: cuál de los trece.
 *
 * ── CADA BOTÓN SE PINTA CON SU PROPIO TEMA ──────────────────────────────────
 *
 * Los bloques de `temas.css` son `[data-tema="…"]` a secas, sin `:root`: valen
 * para cualquier elemento que lleve el atributo. Así que cada botón lo lleva,
 * y adentro suyo `--bg`, `--acc` o `--fTit` son los de ESE tema: la muestra es
 * el tema de verdad —su fondo, su letra, su borde—, no un cuadradito de color
 * elegido a mano que mañana no coincide.
 *
 * Una variante («Venom (cara)», ver `VARIANTES_DE_TEMA`) lleva el `data-tema`
 * de su tema —se pinta igual— y su `data-variante`, que le cambia el símbolo:
 * la cara en vez del logo. Marcado va el que coincide con la OPCIÓN, así que
 * de los dos Venom se marca uno solo.
 *
 * ── DE DÓNDE SALE CUÁL ESTÁ PUESTO ──────────────────────────────────────────
 *
 * El tema lo maneja `LayoutSwitcher`, que es quien lo guarda y lo pone en la
 * raíz. Lo comparte por contexto; fuera de él —producción, que no tiene
 * `LayoutSwitcher`— no hay contexto y esto no dibuja nada.
 */
export const TemaUatContexto = createContext(null);

const SelectorDeTemas = () => {
  const ctx = useContext(TemaUatContexto);
  if (!ctx) return null;
  const { tema, setTema, armazon, setArmazon, vidrio, verVidrio, fijarVidrio } = ctx;

  return (
    <div className="hub-pref tema-sel">
      <div className="et">Tema</div>
      <div className="tema-sel-grilla" role="group" aria-label="Tema">
        {OPCIONES_DE_TEMA.map(([id, label]) => (
          <button
            key={id}
            type="button"
            className="tema-op"
            data-tema={temaDeLaOpcion(id)}
            data-variante={varianteDeLaOpcion(id) || undefined}
            aria-pressed={tema === id}
            onClick={() => setTema(id)}
          >
            <span className="tema-op-nombre">{label}</span>
            <span className="tema-op-muestra" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
          </button>
        ))}
      </div>
      {/* DÓNDE VA EL MENÚ. Era un panel flotante de prueba («Armazón: Barra /
          Rail»), que en producción lo habría visto todo el mundo encima de
          su pantalla. Con Estándar no se ofrece: el hub sin tema es el del
          menú al costado, y la barra de arriba está dibujada con el tema. */}
      {tema !== TEMA_ESTANDAR && setArmazon && (
        <>
          <div className="et tema-vista-et">Menú</div>
          <div className="tema-vista" role="group" aria-label="Menú">
            <button
              type="button"
              aria-pressed={armazon !== "barra"}
              onClick={() => setArmazon("rail")}
            >
              Al costado
            </button>
            <button
              type="button"
              aria-pressed={armazon === "barra"}
              onClick={() => setArmazon("barra")}
            >
              Arriba
            </button>
          </div>
        </>
      )}
      {/* CUÁN OPACAS SON LAS TARJETAS. Igna (10/10): «que haya un selector
          para elegir qué tan opacas querés las tarjetas, y que cambie». Se ve
          al arrastrar y se guarda al soltar. Sólo en los temas con fondo. */}
      {CON_FONDO.includes(temaDeLaOpcion(tema)) && fijarVidrio && (
        <>
          <div className="et tema-vista-et">Opacidad de las tarjetas</div>
          <div className="tema-vidrio">
            <input
              type="range"
              min={VIDRIO_MIN}
              max={100}
              step={5}
              value={vidrio}
              aria-label="Opacidad de las tarjetas"
              onChange={(e) => verVidrio(e.target.value)}
              onPointerUp={(e) => fijarVidrio(e.target.value)}
              onKeyUp={(e) => fijarVidrio(e.target.value)}
              onBlur={(e) => fijarVidrio(e.target.value)}
            />
            <output>{vidrio} %</output>
          </div>
        </>
      )}
    </div>
  );
};

export default SelectorDeTemas;
